/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DomainIcon from "@mui/icons-material/Domain";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { AdHocEditControls, isAdHocEditable } from "./AdHocEdit";
import {
  CompanyResolution,
  CompanyResolutionActions,
  isAccountResolvedField,
  requiresAccountResolution,
} from "./CompanyResolution";
import { useFocusReturn } from "./helpers/useFocusReturn";
import { useTheme } from "@mui/material/styles";
import { compareStrings } from "../suggested-changes/CellRenderers";
import { EmailVerdict, VerifyEmailButton } from "./EmailVerification";
import {
  describeWriteAction,
  isEmailField,
  isEmailWriteBlocked,
  useEmailVerificationVersion,
} from "./helpers/emailVerification";
import { rampIconColor } from "./helpers/rampColor";
import {
  CandidateValueGroup,
  getFieldVariant,
  groupCandidates,
  LedgerField,
} from "./helpers/normalize";
import { SourceChip } from "./SourceChip";
import { ConfidenceBand } from "./ConfidenceBand";
import classes from "./css/recordReview.module.css";

/*
 * One field of the ledger, whatever it carries: a single suggestion, an
 * agreement between sources, or a contest the steward must resolve. The field
 * is the unit of review, so competing values from different systems land in
 * the same row instead of in separate tabs - which is the whole point of this
 * surface.
 */

/*
 * Approving takes the ramp's top band rather than a fresh green, so the
 * surface still holds exactly one source of colour meaning.
 */
const approveColor = rampIconColor("Outstanding");

/**
 * A blank cell and a missing value are different facts, so an absent value is
 * the literal word in italic grey (the comparison-table signature).
 */
export const ValueOrEmpty = (props: {
  value: string | null | undefined;
  isTextCompany?: boolean;
  /** No source could vouch for this field - say so rather than say "empty". */
  unknown?: boolean;
  /**
   * Empty on a field the ledger is already reviewing. Only there has the
   * stronger word been earned: "missing" says a value belongs here, which is
   * a claim about the field, and Cirrom raising the field is what earns it.
   * In the full record the same word would call Suffix and Middle Name
   * deficient on records where they are legitimately absent.
   */
  missing?: boolean;
  /**
   * What "empty" means on a surface that is not reading the CRM. The default
   * sentence names the CRM, which is true in the review card and false in the
   * account card's source panels, where the blank belongs to an enrichment
   * source - so the word stays shared and the fact stays local.
   */
  emptyTooltip?: string;
}) => {
  if (!props.value) {
    const label = props.unknown
      ? "unknown"
      : props.missing
      ? "missing"
      : "empty";
    return (
      <Tooltip
        title={
          props.unknown
            ? "This field could not be loaded, so its value is unknown."
            : props.missing
            ? "Your CRM holds nothing for this field."
            : props.emptyTooltip ?? "This field holds no value in your CRM."
        }
      >
        <Typography
          variant="body2"
          className={classes.cellValue}
          sx={{
            color: props.missing ? rampIconColor("Critical") : "text.secondary",
            fontStyle: "italic",
          }}
        >
          {label}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={props.value}>
      <Typography variant="body2" className={classes.cellValue}>
        {props.isTextCompany && (
          <Tooltip title="Company name stored as text in InterAction.">
            <WarningIcon
              className={classes.fieldLabelIcon}
              sx={{
                color: "text.secondary",
                verticalAlign: "text-bottom",
                mr: 1,
              }}
            />
          </Tooltip>
        )}
        {props.value}
      </Typography>
    </Tooltip>
  );
};

/*
 * The written support for a candidate, split by kind: URLs become the
 * "profile" link, prose becomes the visible evidence line beneath the value.
 * Provenance the steward has to hover for is provenance half of them never
 * read - so the "why" is on the surface, not in a tooltip.
 */
const getEvidenceParts = (group: CandidateValueGroup) => {
  const texts = group.candidates
    .map((candidate) => candidate.evidence)
    .filter(Boolean) as string[];
  return {
    prose: texts.filter((text) => !text.startsWith("http")).join(" · "),
    url: texts.find((text) => text.startsWith("http")),
  };
};

/*
 * The character-level diff of a change, shown inside the value's tooltip -
 * inline highlighting proved hard to read on real values, and the tooltip is
 * where the suggested-changes grid keeps its diff too. Same two-cue treatment
 * as that grid (fill AND underline/strikethrough, colours through
 * contrastText) so the vocabulary matches across surfaces.
 */
const DiffTooltipContent = (props: {
  oldValue: string;
  newValue: string;
  evidence?: string;
}) => {
  const theme = useTheme();
  const diffStyles = {
    added: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
      textDecoration: "underline",
    },
    removed: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      textDecoration: "line-through",
    },
  };
  return (
    /*
     * Body size rather than the tooltip's default caption: the diff is read
     * character by character, and 11px is too tight for that job.
     */
    <div style={{ whiteSpace: "pre-wrap", fontSize: "13px" }}>
      <div>
        {compareStrings(props.oldValue, props.newValue).map((change, index) => {
          const character = change.charAt(change.length - 1);
          if (change.charAt(0) === "+") {
            return (
              <span key={index} style={diffStyles.added} aria-label="added">
                {character}
              </span>
            );
          }
          if (change.charAt(0) === "-") {
            return (
              <span key={index} style={diffStyles.removed} aria-label="removed">
                {character}
              </span>
            );
          }
          return <span key={index}>{character}</span>;
        })}
      </div>
      {props.evidence && <div>{props.evidence}</div>}
    </div>
  );
};

const CandidateValue = (props: {
  group: CandidateValueGroup;
  crmValue: string | null;
  showSources: boolean;
}) => {
  const { group } = props;
  const confidence = group.candidates.find(
    (candidate) => candidate.confidence !== undefined
  )?.confidence;
  const { prose: evidence, url: evidenceUrl } = getEvidenceParts(group);

  const valueText = group.value ? (
    /*
     * The proposed value is what the steward is here to judge, so it carries
     * the row's weight; labels and incumbent values stay quiet around it.
     */
    <Typography
      variant="body2"
      className={classes.cellValue}
      sx={{ fontWeight: 600 }}
    >
      {group.value}
    </Typography>
  ) : (
    /*
     * A change with no proposed value carries only its description (e.g.
     * "company field is not populated") - show that instead of a blank.
     */
    <Typography
      variant="body2"
      className={classes.cellValue}
      sx={{ color: "text.secondary", fontStyle: "italic" }}
    >
      {evidence || "no value proposed"}
    </Typography>
  );

  const tooltipTitle =
    props.crmValue && group.value ? (
      <DiffTooltipContent
        oldValue={props.crmValue}
        newValue={group.value}
        evidence={evidence || undefined}
      />
    ) : (
      evidence || group.value
    );

  return (
    <>
      <Tooltip title={tooltipTitle}>{valueText}</Tooltip>
      {/*
       * Provenance chips only when they add something. Now that the column
       * itself says "Cirrom proposes", repeating "Cirrom" on every row of the
       * common single-source case is noise around the value. The moment a
       * field draws on more than one source - a contest, an agreement - every
       * value needs its chip back, because there the source is the whole
       * point of the row.
       */}
      {props.showSources &&
        group.sources.map((source) => (
          <SourceChip key={source} source={source} />
        ))}
      {evidenceUrl && (
        <Link
          href={evidenceUrl}
          target="_blank"
          rel="noopener"
          variant="body2"
          underline="hover"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          profile
        </Link>
      )}
      {confidence !== undefined && <ConfidenceBand confidence={confidence} />}
    </>
  );
};

export interface LedgerRowActions {
  disabled: boolean;
  /** Field whose decision is being held in the undo window, if any. */
  queuedFieldName?: string;
  onApproveGroup: (field: LedgerField, group: CandidateValueGroup) => void;
  onRejectGroup: (field: LedgerField, group: CandidateValueGroup) => void;
  /** Absent when ad-hoc editing is unavailable (no explain payload loaded). */
  onAdHocApprove?: (field: LedgerField, newValue: string) => void;
  /**
   * Company approvals resolve to a CRM account rather than to the proposed
   * text. Absent when the record or system is not known, in which case the
   * company row simply offers no approval at all - writing the name would
   * leave the contact unlinked, which is worse than leaving it pending.
   */
  companyResolution?: CompanyResolutionActions;
  /**
   * The system the record lives in, recorded on any email verification a
   * row asks for. Optional: a verdict is the address's, not the system's.
   */
  integratedSystemId?: number | null;
  /** The record's CRM contact id, linked to any email verification a row asks for. */
  crmContactId?: string | null;
}

export const LedgerRow = (props: {
  field: LedgerField;
  actions: LedgerRowActions;
}) => {
  const { field, actions } = props;
  const [isEditing, setIsEditing] = useState(false);
  const focus = useFocusReturn();
  // Every way out of the editor puts focus back on the pencil that opened it.
  const closeEditor = () => {
    setIsEditing(false);
    focus.returnTo(field.fieldName);
  };
  const variant = getFieldVariant(field);
  const groups = groupCandidates(field.candidates);
  const isChoice = variant === "contested" || variant === "multipleChoice";
  /*
   * Company is edited through the account picker, never a text box. On a row
   * where a proposed value is already being resolved that picker is on screen
   * anyway, so the pencil would open a second one - it appears only where the
   * proposal came resolved and the steward wants a different account.
   */
  const editsThroughAccount =
    isAccountResolvedField(field.fieldName) &&
    Boolean(actions.companyResolution) &&
    !groups.some((group) => requiresAccountResolution(field, group));
  const canEdit =
    editsThroughAccount ||
    (Boolean(actions.onAdHocApprove) && isAdHocEditable(field.fieldName));
  /*
   * A decision on this field is waiting out its undo window. Taking another
   * would commit the first and queue a duplicate, so the row holds still
   * until the window closes.
   */
  const actionsDisabled =
    actions.disabled || actions.queuedFieldName === field.fieldName;
  /*
   * The column header already says these came from Cirrom, so a chip on every
   * single-source row repeats it. Once a field draws on more than one source,
   * or on a source other than Cirrom, the chips come back on every value.
   */
  const fieldSources = new Set(field.candidates.map((c) => c.source));
  const showSources = fieldSources.size > 1 || !fieldSources.has("cirrom");
  /*
   * A proposed email can be checked with Allegrow before it is approved. The
   * approve control reads the verdict through plain getters, so the row
   * subscribes here to re-render when one lands.
   */
  const verifiesEmail = isEmailField(field.fieldName);
  useEmailVerificationVersion();
  const verificationContext = {
    crmContactId: actions.crmContactId,
    integratedSystemId: actions.integratedSystemId,
  };

  return (
    <div className={classes.ledgerRow}>
      <div className={classes.fieldLabel}>
        {field.fromAccount && (
          <Tooltip title="Field linked from account">
            <DomainIcon
              className={classes.fieldLabelIcon}
              sx={{ color: "text.secondary" }}
            />
          </Tooltip>
        )}
        <Tooltip title={field.altText ?? ""}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {field.label}
          </Typography>
        </Tooltip>
      </div>

      <div className={classes.cellWithEdit}>
        {/*
         * Every row in the ledger is a field Cirrom has something to say
         * about, so a blank here is a gap whether or not the proposal
         * carries a value - the country it could not derive is still a
         * country the record lacks. One word for the whole column.
         */}
        <ValueOrEmpty
          value={field.crmValue}
          isTextCompany={field.crmValueIsTextCompany}
          unknown={field.valueUnknown}
          missing
        />
        {canEdit && !isEditing && (
          <Tooltip
            title={
              editsThroughAccount
                ? `Choose a different account for ${field.label}`
                : `Enter your own value for ${field.label}`
            }
          >
            <span className={classes.fieldEditSlot}>
              <IconButton
                size="small"
                className={classes.fieldEditButton}
                disabled={actionsDisabled}
                aria-label={`Modify ${field.label}`}
                ref={focus.register(field.fieldName)}
                sx={{ color: "text.secondary" }}
                onClick={() => setIsEditing(true)}
              >
                <DriveFileRenameOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>

      <div className={classes.candidateStack}>
        {isEditing &&
          (editsThroughAccount ? (
            <CompanyResolution
              field={field}
              disabled={actionsDisabled}
              actions={actions.companyResolution!}
              onClose={closeEditor}
            />
          ) : (
            <AdHocEditControls
              label={field.label}
              initialValue={field.crmValue ?? ""}
              disabled={actionsDisabled}
              fieldName={field.fieldName}
              verification={verificationContext}
              onSubmit={(value) => {
                actions.onAdHocApprove!(field, value);
                closeEditor();
              }}
              onCancel={closeEditor}
            />
          ))}
        {isChoice && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {variant === "contested"
              ? `${groups.length} sources disagree — one value must be chosen`
              : `${groups.length} possible matches — one must be chosen`}
          </Typography>
        )}
        {groups.map((group, index) => {
          /*
           * A company is not a string the CRM can hold - it is a link to an
           * account. So this value's approval is a picker rather than a
           * check, and the check belongs with the picker.
           */
          const needsAccount = requiresAccountResolution(field, group);
          return (
            <Box
              key={`${field.fieldName}-${index}`}
              className={
                isChoice ? classes.candidateOption : classes.candidateLine
              }
              sx={isChoice ? { borderColor: "divider" } : undefined}
            >
              <div className={classes.candidateMain}>
                <CandidateValue
                  group={group}
                  crmValue={field.crmValue}
                  showSources={showSources}
                />
                <div className={classes.candidateActions}>
                  {/*
                   * The check precedes the decision, so it leads the cluster.
                   * Only a value that could be written is worth checking.
                   */}
                  {verifiesEmail && group.value && !needsAccount && (
                    <VerifyEmailButton
                      email={group.value}
                      context={verificationContext}
                      disabled={actionsDisabled}
                    />
                  )}
                  {/*
                   * Neutral, matching the Reject all button and its reasoning:
                   * error red frames rejecting as a mistake, when turning down
                   * a bad suggestion is the steward doing the job. It also
                   * stopped red - four of them down one column - from
                   * out-shouting the amber approve beside it, which is the
                   * action with the consequence.
                   */}
                  <Tooltip title="Reject. Cirrom won't suggest it again for this record.">
                    <span>
                      <IconButton
                        size="small"
                        disabled={actionsDisabled}
                        aria-label={`Reject suggested ${field.label}`}
                        onClick={() => actions.onRejectGroup(field, group)}
                        sx={{
                          color: "text.secondary",
                          "&:hover": { color: "text.primary" },
                        }}
                      >
                        <CancelOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {/*
                   * No approve here without a value to write, and none for a
                   * company: a description-only change ("company field is not
                   * populated") and a detected employer both resolve through
                   * the account picker below, which carries its own write.
                   */}
                  {group.value && !needsAccount && (
                    <Tooltip
                      title={
                        verifiesEmail
                          ? describeWriteAction(
                              group.value,
                              "Write this change to your CRM",
                              "approved",
                              "Reject it or edit the address."
                            )
                          : "Write this change to your CRM"
                      }
                    >
                      <span>
                        {/*
                         * The ramp's top band, not amber. Amber is the action
                         * colour by the book, but at row scale it read as one
                         * more amber thing rather than as the control that
                         * writes, and primary.main missed the 3:1 floor for a
                         * non-text control anyway.
                         *
                         * Green here is still the one ramp - the same value the
                         * "Outstanding" swatch uses - so no new hue enters the
                         * surface. Amber stays rationed to the bulk commit in
                         * the footer, which is the surface's single loudest
                         * action.
                         */}
                        <IconButton
                          size="small"
                          /*
                           * A dead or bouncing address loses its approve: the
                           * write has no undo, and Allegrow has already
                           * answered the question the steward would be
                           * guessing at. Reject stays, and so does the pencil.
                           */
                          disabled={
                            actionsDisabled ||
                            (verifiesEmail && isEmailWriteBlocked(group.value))
                          }
                          aria-label={`Approve suggested ${field.label}`}
                          onClick={() => actions.onApproveGroup(field, group)}
                          sx={{ color: approveColor }}
                        >
                          <CheckCircleOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
              {verifiesEmail && group.value && (
                <EmailVerdict
                  email={group.value}
                  context={verificationContext}
                />
              )}
              {needsAccount && actions.companyResolution && (
                <CompanyResolution
                  field={field}
                  group={group}
                  disabled={actionsDisabled}
                  actions={actions.companyResolution}
                />
              )}
            </Box>
          );
        })}
        {variant === "agreement" && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Both sources agree on this value
          </Typography>
        )}
      </div>
    </div>
  );
};
